/**
 * Lineage History Panel (DC-872)
 *
 * Visualizes the history of lineage events over time.
 * Shows a timeline view of data provenance events with filtering.
 *
 * Features:
 * - Timeline visualization of lineage events
 * - Filtering by event type and source
 * - Date range selection
 * - Event details on click
 *
 * @see Documentation/Development/Design Docs/Sprint 11/Data_Lineage_and_Provenance_Design.md
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import './lineage-history.css';

// ============================================================================
// TYPES
// ============================================================================

interface LineageEvent {
  event_id: string;
  journey_id: string;
  event_type: 'finding' | 'synthesis' | 'recommendation' | 'valuation';
  event_source: string;
  content_hash: string;
  content_payload: Record<string, unknown>;
  parent_event_ids: string[];
  source_version: number;
  created_at: string;
  created_by: string | null;
}

interface LineageHistoryPanelProps {
  journeyId: string;
  onEventSelect?: (event: LineageEvent) => void;
}

// Event type colors for visualization
const EVENT_TYPE_COLORS: Record<string, { bg: string; border: string; icon: string }> = {
  finding: { bg: 'var(--tri-teal)', border: 'var(--tri-teal)', icon: '🔍' },
  synthesis: { bg: 'var(--tri-soft)', border: 'var(--tri-soft)', icon: '⚗️' },
  recommendation: { bg: 'var(--tri-good)', border: 'var(--tri-good)', icon: '💡' },
  valuation: { bg: 'rgba(var(--tri-terra-rgb), 0.12)', border: 'var(--tri-terra)', icon: '💰' },
};

// Event source icons
const SOURCE_ICONS: Record<string, string> = {
  data_quality: '📊',
  ml_readiness: '🤖',
  ontology_inference: '🧬',
  live_checks: '✅',
  privacy: '🔒',
  alcoa: '📋',
  fda_credibility: '🏛️',
  bridge_ai: '🌉',
  action_plan: '📈',
};

// ============================================================================
// COMPONENT
// ============================================================================

export const LineageHistoryPanel: React.FC<LineageHistoryPanelProps> = ({
  journeyId,
  onEventSelect,
}) => {
  const { authFetch } = useApiClient();
  const [events, setEvents] = useState<LineageEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<LineageEvent | null>(null);

  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState<string | null>(null);
  const [eventSourceFilter, setEventSourceFilter] = useState<string | null>(null);
  const [dateRange, _setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (eventTypeFilter) params.append('event_type', eventTypeFilter);
        if (eventSourceFilter) params.append('event_source', eventSourceFilter);
        if (dateRange.start) params.append('start_date', dateRange.start);
        if (dateRange.end) params.append('end_date', dateRange.end);
        params.append('limit', '200');

        const response = await authFetch(
          `/api/lineage/${journeyId}?${params.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch lineage events: ${response.status}`);
        }

        const data = await response.json();
        setEvents(data.events || []);
      } catch (err) {
        console.error('[LineageHistory] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load lineage history');
      } finally {
        setLoading(false);
      }
    };

    if (journeyId) {
      fetchEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [journeyId, eventTypeFilter, eventSourceFilter, dateRange]);

  // Group events by date for timeline
  const groupedByDate = events.reduce((acc, event) => {
    const date = new Date(event.created_at).toLocaleDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {} as Record<string, LineageEvent[]>);

  // Get unique event types and sources for filters
  const eventTypes = [...new Set(events.map(e => e.event_type))];
  const eventSources = [...new Set(events.map(e => e.event_source))];

  const handleEventClick = (event: LineageEvent) => {
    setSelectedEvent(event);
    onEventSelect?.(event);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="lineage-history-panel loading">
        <div className="loading-spinner">Loading lineage history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lineage-history-panel error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="lineage-history-panel">
      {/* Header */}
      <div className="lineage-history-header">
        <h3>Lineage History</h3>
        <span className="event-count">{events.length} events</span>
      </div>

      {/* Filters */}
      <div className="lineage-filters">
        <div className="filter-group">
          <label>Event Type</label>
          <select
            value={eventTypeFilter || ''}
            onChange={(e) => setEventTypeFilter(e.target.value || null)}
          >
            <option value="">All Types</option>
            {eventTypes.map((type) => (
              <option key={type} value={type}>
                {EVENT_TYPE_COLORS[type]?.icon || '📌'} {type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Source</label>
          <select
            value={eventSourceFilter || ''}
            onChange={(e) => setEventSourceFilter(e.target.value || null)}
          >
            <option value="">All Sources</option>
            {eventSources.map((source) => (
              <option key={source} value={source}>
                {SOURCE_ICONS[source] || '📦'} {source.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Timeline */}
      <div className="lineage-timeline">
        {Object.entries(groupedByDate).length === 0 ? (
          <div className="no-events">
            <span className="no-events-icon">📭</span>
            <p>No lineage events found</p>
          </div>
        ) : (
          Object.entries(groupedByDate)
            .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
            .map(([date, dateEvents]) => (
              <div key={date} className="timeline-day">
                <div className="timeline-date">{date}</div>
                <div className="timeline-events">
                  {dateEvents
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .map((event) => {
                      const colors = EVENT_TYPE_COLORS[event.event_type] || {
                        bg: 'var(--tri-ink)',
                        border: 'var(--tri-taupe)',
                        icon: '📌',
                      };
                      const isSelected = selectedEvent?.event_id === event.event_id;

                      return (
                        <div
                          key={event.event_id}
                          className={`timeline-event ${isSelected ? 'selected' : ''}`}
                          style={{
                            backgroundColor: colors.bg,
                            borderColor: colors.border,
                          }}
                          onClick={() => handleEventClick(event)}
                        >
                          <div className="event-time">{formatTime(event.created_at)}</div>
                          <div className="event-icon">{colors.icon}</div>
                          <div className="event-content">
                            <div className="event-type">{event.event_type}</div>
                            <div className="event-source">
                              {SOURCE_ICONS[event.event_source] || '📦'} {event.event_source.replace(/_/g, ' ')}
                            </div>
                            {event.parent_event_ids.length > 0 && (
                              <div className="event-parents">
                                ↳ {event.parent_event_ids.length} parent{event.parent_event_ids.length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
        )}
      </div>

      {/* Event Details */}
      {selectedEvent && (
        <div className="event-details-panel">
          <div className="event-details-header">
            <h4>Event Details</h4>
            <button className="close-btn" onClick={() => setSelectedEvent(null)}>
              ×
            </button>
          </div>
          <div className="event-details-content">
            <div className="detail-row">
              <span className="detail-label">Event ID:</span>
              <code className="detail-value">{selectedEvent.event_id}</code>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type:</span>
              <span className="detail-value">
                {EVENT_TYPE_COLORS[selectedEvent.event_type]?.icon} {selectedEvent.event_type}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Source:</span>
              <span className="detail-value">
                {SOURCE_ICONS[selectedEvent.event_source]} {selectedEvent.event_source}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Created:</span>
              <span className="detail-value">
                {new Date(selectedEvent.created_at).toLocaleString()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Version:</span>
              <span className="detail-value">v{selectedEvent.source_version}</span>
            </div>
            {selectedEvent.parent_event_ids.length > 0 && (
              <div className="detail-row">
                <span className="detail-label">Parents:</span>
                <span className="detail-value">{selectedEvent.parent_event_ids.length} events</span>
              </div>
            )}
            <div className="detail-row payload">
              <span className="detail-label">Payload:</span>
              <pre className="detail-value">
                {JSON.stringify(selectedEvent.content_payload, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineageHistoryPanel;
